import {computed, inject, Injectable, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {CurrentUserContextService} from '../../shared/services/current-user-context.service';
import {RoleAssignApiService} from '../role-assign/role-assign-api.service';
import {RoleAssignmentDto, TradeBusinessOptionDto} from '../../../models/generated-models';
import {TradeApiService} from './trade-api.service';

const ACTIVE_ASSIGNMENT_STORAGE_KEY = 'agent-harvest.active-role-assignment-id';
const SELECTED_BUSINESS_STORAGE_PREFIX = 'trade.selected-business.';

const isUsableAssignment = (item: RoleAssignmentDto): boolean =>
  item.active && (item.invitationStatus === 'ACCEPTED' || item.status === 'ACTIVE');

/**
 * App-wide state for the trade module: which of my businesses is selected and
 * whether I am acting under a granted role assignment. Also the single owner of
 * the "act on behalf of" selection used by the account switcher and the
 * acting-as banner (previously `FarmerHarvestStore`).
 */
@Injectable({providedIn: 'root'})
export class TradeStore {
  private readonly currentUserContext = inject(CurrentUserContextService);
  private readonly roleAssignApi = inject(RoleAssignApiService);
  private readonly tradeApi = inject(TradeApiService);

  // --- act-as / role assignment ------------------------------------------
  readonly currentUserId = signal<number | null>(null);
  readonly activeRoleAssignment = signal<RoleAssignmentDto | null>(null);
  readonly receivedAssignments = signal<RoleAssignmentDto[]>([]);
  readonly assignmentsLoading = signal(false);
  private assignmentsRestoreStarted = false;
  /** Settles once the act-as restore has answered (either way); see {@link loadBusinesses}. */
  private assignmentRestoreDone = Promise.resolve();
  private settleAssignmentRestore: () => void = () => undefined;
  /** Settles once the current user's id is known (or known to be unavailable). */
  private readonly currentUserReady: Promise<void>;

  /** The user the records belong to: the grantor while acting under a grant. */
  readonly effectiveUserId = computed<number | null>(
    () => this.activeRoleAssignment()?.grantorUserId ?? this.currentUserId()
  );

  // --- businesses ---------------------------------------------------------
  readonly businesses = signal<TradeBusinessOptionDto[]>([]);
  readonly businessesLoaded = signal(false);
  readonly loading = signal(false);
  readonly selectedBusinessId = signal<number | null>(null);

  readonly selectedBusiness = computed<TradeBusinessOptionDto | null>(() => {
    const id = this.selectedBusinessId();
    if (id == null) {
      return null;
    }
    return this.businesses().find((item) => item.id === id) ?? null;
  });

  /**
   * Any role may start a chain with an origin lot — the product owner's optimistic model. A lot
   * that did not come from a farm is not refused; the journey graph shows where it started. The
   * signal stays so the template has one place to gate the button should a rule come back.
   */
  readonly canRecordHarvest = computed<boolean>(() => true);

  constructor() {
    this.currentUserReady = this.bootstrapCurrentUser();
  }

  // --- businesses ---------------------------------------------------------

  /**
   * Loads the selectable businesses and restores (or auto-picks) the selection.
   *
   * The remembered selection is keyed by the effective user, so this waits until the
   * current user and the act-as restore have answered — after an account switch the page
   * reloads and both arrive asynchronously; applying the selection before that read (and
   * wrote) the memory under the wrong key, which is how the switcher ended up on an empty
   * business with every business's stock showing.
   */
  loadBusinesses(): Promise<TradeBusinessOptionDto[]> {
    this.loading.set(true);
    return Promise.all([
      firstValueFrom(this.tradeApi.listMyBusinesses()),
      this.currentUserReady,
      this.whenActAsRestored()
    ])
      .then(([items]) => {
        const businesses = items ?? [];
        this.businesses.set(businesses);
        this.applySelection(businesses);
        return businesses;
      })
      .catch(() => {
        this.businesses.set([]);
        this.selectedBusinessId.set(null);
        return [] as TradeBusinessOptionDto[];
      })
      .finally(() => {
        this.businessesLoaded.set(true);
        this.loading.set(false);
      });
  }

  selectBusiness(businessId: number | null): void {
    this.selectedBusinessId.set(businessId);
    writeSelectedBusinessId(this.storageUserKey(), businessId);
  }

  private applySelection(businesses: TradeBusinessOptionDto[]): void {
    if (businesses.length === 0) {
      this.selectedBusinessId.set(null);
      return;
    }
    if (businesses.length === 1) {
      this.selectBusiness(businesses[0].id ?? null);
      return;
    }
    const current = this.selectedBusinessId();
    if (current != null && businesses.some((item) => item.id === current)) {
      return;
    }
    const remembered = readSelectedBusinessId(this.storageUserKey());
    const restored = remembered != null && businesses.some((item) => item.id === remembered)
      ? remembered
      : null;
    this.selectedBusinessId.set(restored);
  }

  /** `trade.selected-business.<userId>` — per user, so a shared device stays sane. */
  private storageUserKey(): string {
    return String(this.effectiveUserId() ?? this.currentUserId() ?? 'anonymous');
  }

  // --- act-as / role assignment ------------------------------------------

  setActiveRoleAssignment(assignment: RoleAssignmentDto | null): void {
    this.activeRoleAssignment.set(assignment);
  }

  /** Switcher selection with backend + localStorage persistence. */
  selectActiveRoleAssignment(assignment: RoleAssignmentDto | null): Promise<void> {
    this.activeRoleAssignment.set(assignment);
    writeCachedActiveAssignmentId(assignment?.id ?? null);
    return new Promise<void>((resolve) => {
      this.roleAssignApi.setActiveAssignment(assignment?.id ?? null).subscribe({
        next: () => resolve(),
        error: () => resolve(),
        complete: () => resolve()
      });
    });
  }

  /**
   * Settles once {@link restoreActiveAssignment} has answered (immediately when it was never
   * started), for pages whose list depends on whose account is active.
   */
  whenActAsRestored(): Promise<void> {
    return this.assignmentsRestoreStarted ? this.assignmentRestoreDone : Promise.resolve();
  }

  /**
   * Restores the persisted "act on behalf of" selection after a refresh.
   * Idempotent — safe to call from every host (layout + switcher). The backend
   * is the source of truth; localStorage only bridges an outage of that call.
   */
  restoreActiveAssignment(): void {
    if (this.assignmentsRestoreStarted) {
      return;
    }
    this.assignmentsRestoreStarted = true;
    this.assignmentRestoreDone = new Promise<void>((resolve) => {
      this.settleAssignmentRestore = resolve;
    });
    this.assignmentsLoading.set(true);
    this.roleAssignApi.listReceived().subscribe({
      next: (items) => {
        const usable = (items ?? []).filter(isUsableAssignment);
        this.receivedAssignments.set(usable);
        this.roleAssignApi.getActiveAssignment().subscribe({
          next: (dto) => {
            if (dto?.id != null && usable.some((item) => item.id === dto.id)) {
              writeCachedActiveAssignmentId(dto.id);
              this.activeRoleAssignment.set(usable.find((item) => item.id === dto.id) ?? null);
            } else {
              // The backend answered and is not acting: believe it, and drop the browser's memory.
              // Restoring the cached grant here made the UI claim "acting" while every list the
              // backend returned was the delegate's own (work item 193).
              writeCachedActiveAssignmentId(null);
              this.activeRoleAssignment.set(null);
            }
            this.assignmentsLoading.set(false);
            this.settleAssignmentRestore();
          },
          error: () => {
            const cached = readCachedActiveAssignmentId();
            this.activeRoleAssignment.set(
              cached == null ? null : usable.find((item) => item.id === cached) ?? null
            );
            this.assignmentsLoading.set(false);
            this.settleAssignmentRestore();
          }
        });
      },
      error: () => {
        this.receivedAssignments.set([]);
        this.assignmentsLoading.set(false);
        this.settleAssignmentRestore();
      }
    });
  }

  private async bootstrapCurrentUser(): Promise<void> {
    try {
      const profile = await firstValueFrom(this.currentUserContext.load());
      this.currentUserId.set(profile.id ?? null);
    } catch {
      this.currentUserId.set(null);
    }
  }
}

function readSelectedBusinessId(userKey: string): number | null {
  try {
    const raw = localStorage.getItem(`${SELECTED_BUSINESS_STORAGE_PREFIX}${userKey}`);
    const id = raw == null ? NaN : Number(raw);
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

function writeSelectedBusinessId(userKey: string, id: number | null): void {
  try {
    const key = `${SELECTED_BUSINESS_STORAGE_PREFIX}${userKey}`;
    if (id == null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(id));
    }
  } catch {
    // private-mode / SSR — the selection simply is not remembered
  }
}

function readCachedActiveAssignmentId(): number | null {
  try {
    const raw = localStorage.getItem(ACTIVE_ASSIGNMENT_STORAGE_KEY);
    const id = raw == null ? NaN : Number(raw);
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

function writeCachedActiveAssignmentId(id: number | null): void {
  try {
    if (id == null) {
      localStorage.removeItem(ACTIVE_ASSIGNMENT_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_ASSIGNMENT_STORAGE_KEY, String(id));
    }
  } catch {
    // private-mode / SSR — backend persistence still applies
  }
}
